/**
 * View component for /user/register
 *
 * On successful registration this component forwards the user back to referrer
 * or to the root if there is no referrer.
 *
 * NOTE: upon reaching this page, user can toggle between /user/login and
 * /user/register without changing the original referring source route.
 */
// import primary libraries
import { useLocation, Link } from 'react-router-dom';

// import user components
import UserLayout from '../components/UserLayout.jsx';
import UserRegisterSubmitEmail from '../components/UserRegisterSubmitEmail.jsx';
import useMarketingContent from '../../../global/utils/useMarketingContent';

const UserRegister = () => {
  const location = useLocation();
  const redirect = new URLSearchParams(location.search).get('redirect');

  // NOTE: For devs doing local testing, if you're running into CORS errors
  //       or not getting the marketing content to show up, try modifying the
  //       useMarketingContent HEAD error handling or having it load the
  //       marketing content from a local running static-marketing server
  const { mobile, urls, errors, headerId, footerId, setSlotError } = useMarketingContent(
    'register'
    , ['header', 'footer', 'background']);

  return (
    <UserLayout
      title="Create an account"
      className="w-screen h-screen overflow-hidden"
    >
      <div className='sm:w-full mx-auto flex flex-col flex-nowrap h-full max-h-full justify-center md:justify-between relative'>
        {/* Marketing Background Image */}
        {urls.background && !mobile && !errors.background && (
          <iframe
            src={urls.background}
            title="Marketing background"
            aria-hidden="true"
            sandbox="allow-scripts allow-same-origin"
            className="hidden md:block w-full h-full absolute inset-0 -z-10 overflow-hidden border-0"
            onError={() => setSlotError('background')}
          />
        )}
        {/* Fallback background - shown if error or no URL */}
        {(errors.background || !urls.background) && !mobile && (
          <div className="block bg-neutral-pri w-full h-full absolute inset-0 -z-10"></div>
        )}

        {/* Header */}
        <div id={headerId} className="flex flex-grow-0 flex-shrink-0 w-full">
          {urls.header && !errors.header && (
            <iframe
              src={urls.header}
              title="Marketing header"
              sandbox="allow-scripts allow-same-origin"
              className="h-full w-full max-h-full max-w-full flex-grow flex-shrink border-0"
              onError={() => setSlotError('header')}
            />
          )}
          {(errors.header || !urls.header) && (
            <div className="h-0 w-full"></div>
          )}
        </div>

        <div className="flex-1 min-h-0 flex flex-nowrap justify-center items-center">
          {/* Image Carousel - Removed for now in favor of the central controls + marketing background.
              To restore: add 'splash' to the useMarketingContent slots array above.
          {urls.splash && !errors.splash && (
            <div className="flex-1 p-3 items-center justify-center hidden md:flex max-w-[70%]">
              <iframe
                src={urls.splash}
                title="Marketing splash image"
                sandbox="allow-scripts allow-same-origin"
                className="w-full h-full border-0"
                onError={() => setSlotError('splash')}
              />
            </div>
          )}
          {(errors.splash || !urls.splash) && (
            <p className="text-center flex justify-center items-center w-full h-full">
              Access your Simulations Plus products by signing in through S+ Cloud
            </p>
          )} */}

          <div className="bg-neutral-pri flex flex-col flex-nowrap flex-grow flex-shrink items-center justify-start max-w-full md:w-[550px] md:max-w-[550px] max-h-full gap-2 p-4 rounded-lg overflow-auto">
            <UserRegisterSubmitEmail
              wrapperClass='w-full'
              title="Create an account for S+ Cloud"
            />

            <div className="flex flex-col flex-nowrap min-h-0 w-full gap-2 text-neutral-sec">
              <p className='text-center mt-4 text-sm flex-grow-0 flex-shrink-0'>
                Already have an account?
                <Link
                  to={{
                    pathname: '/user/login'
                    , search: redirect ? `?redirect=${encodeURIComponent(redirect)}` : undefined
                    , state: location.state
                  }}
                  className='mx-2'
                >
                  Sign in
                </Link>
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        {!mobile && (
          <div id={footerId} className="hidden md:flex flex-shrink-0">
            {urls.footer && !errors.footer && (
              <iframe
                src={urls.footer}
                title="Marketing footer"
                sandbox="allow-scripts allow-same-origin"
                className="max-h-full max-w-full flex-grow flex-shrink border-0"
                onError={() => setSlotError('footer')}
              />
            )}
            {(errors.footer || !urls.footer) && (
              <div className="h-0 w-full"></div>
            )}
          </div>
        )}
      </div>
    </UserLayout>
  )
}

export default UserRegister
